import React, { useState, useMemo, useEffect, ChangeEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, TextField, Select, MenuItem, FormHelperText, FormControl, Input, IconButton, CardMedia } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGetCategories } from "../../api/hooks/useCategory";
import { useCreateCommunityGroup, useUpdateCommunityGroup, useGetCommunityGroup } from "../../api/hooks/useCommunity";
import { uploadService } from "../../api/services/uploadService";
import { showSuccess, showError } from "../../helpers/toast";
import EditText from "../../components/editText";
import type { Category } from "../../api/types/category.types";
import type { CommunityGroup } from "../../api/types/community.types";

const AddCommunityGroup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get group data from navigation state or location state
  const groupData = (location.state && location.state.group) as CommunityGroup | undefined;
  const groupId = groupData?._id;
  const isEditMode = !!groupId;

  const [groupName, setGroupName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("default");
  const [status, setStatus] = useState(true);
  const [members, setMembers] = useState("");
  const [image, setImage] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);



  // Form errors state
  const [errors, setErrors] = useState<{
    groupName?: string;
    description?: string;
    category?: string;
    members?: string;
  }>({});

  // Validation functions
  const validateGroupName = (value: string): string => {
    if (!value.trim()) {
      return "Group Name is required";
    }
    if (value.trim().length < 3) {
      return "Group Name must be at least 3 characters";
    }
    if (value.trim().length > 100) {
      return "Group Name must be less than 100 characters";
    }
    return "";
  };

  const validateDescription = (value: string): string => {
    const stripped = (value || "").replace(/<[^>]*>/g, "").trim();
    if (!stripped) {
      return "Description is required";
    }
    if (stripped.length < 10) {
      return "Description must be at least 10 characters";
    }
    if ((value || "").length > 10000) {
      return "Description is too long";
    }
    return "";
  };

  const validateCategory = (value: string): string => {
    if (value === "default") {
      return "Please select a category";
    }
    return "";
  };

  const validateMembers = (value: string): string => {
    if (!value.trim()) {
      return "Members Count is required";
    }
    if (isNaN(Number(value))) {
      return "Members Count must be a valid number";
    }
    if (Number(value) < 0) {
      return "Members Count cannot be negative";
    }
    if (!Number.isInteger(Number(value))) {
      return "Members Count must be a whole number";
    }
    return "";
  };

  // Validate all fields
  const validateAllFields = (): boolean => {
    const newErrors: typeof errors = {};

    const groupNameError = validateGroupName(groupName);
    if (groupNameError) newErrors.groupName = groupNameError;

    const descriptionError = validateDescription(description);
    if (descriptionError) newErrors.description = descriptionError;

    const categoryError = validateCategory(category);
    if (categoryError) newErrors.category = categoryError;

    const membersError = validateMembers(members);
    if (membersError) newErrors.members = membersError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch group data for edit mode
  const { data: groupDetailData, isLoading: isLoadingGroup } = useGetCommunityGroup(
    groupId || '',
    { enabled: isEditMode && !!groupId }
  );

  // Fetch categories from API
  const { data: categoriesData, isLoading: isLoadingCategories } = useGetCategories(
    {},
    { enabled: true }
  );

  // Extract categories from response
  const categories = useMemo(() => {
    const data = categoriesData?.data;
    let cats: Category[] = [];
    
    if (Array.isArray(data)) {
      cats = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        cats = dataObj.list;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        cats = dataObj.data;
      }
    }
    
    // Filter only active categories
    return cats.filter((cat) => cat.status && !cat.isDeleted);
  }, [categoriesData]);

  // Helper function to extract category ID (handles both string and object)
  const getCategoryId = (categoryId: any): string => {
    if (!categoryId) return "default";
    if (typeof categoryId === "string") return categoryId;
    if (typeof categoryId === "object" && categoryId._id) return categoryId._id;
    return "default";
  };

  // Populate form fields when in edit mode - use state data immediately
  useEffect(() => {
    if (isEditMode && groupData) {
      setGroupName(groupData.name || "");
      setDescription(groupData.description || "");
      setCategory(getCategoryId(groupData.categoryId));
      setMembers(groupData.memberCount?.toString() || "");
      setStatus(groupData.status ?? true);
      setImage(groupData.image || "");
      // Clear any existing errors when loading edit data
      setErrors({});
    }
  }, [isEditMode, groupData]);

  // Update form fields when API data is fetched (in case state data is incomplete)
  useEffect(() => {
    if (isEditMode && groupDetailData?.data) {
      const group = groupDetailData.data;
      setGroupName(group.name || "");
      setDescription(group.description || "");
      setCategory(getCategoryId(group.categoryId));
      setMembers(group.memberCount?.toString() || "");
      setStatus(group.status ?? true);
      setImage(group.image || "");
      // Clear any existing errors when loading API data
      setErrors({});
    }
  }, [isEditMode, groupDetailData]);

  // Create community group mutation
  const { mutate: createCommunityGroup, isPending: isCreating } = useCreateCommunityGroup({
    onSuccess: (response, variables, context) => {
      showSuccess(response.message || "Community group created successfully");
      // Small delay to ensure query invalidation completes before navigation
      setTimeout(() => {
        navigate("/Community");
      }, 100);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to create community group");
    },
  });

  // Update community group mutation
  const { mutate: updateCommunityGroup, isPending: isUpdating } = useUpdateCommunityGroup({
    onSuccess: (response, variables, context) => {
      showSuccess(response.message || "Community group updated successfully");
      // Small delay to ensure query invalidation completes before navigation
      setTimeout(() => {
        navigate("/Community");
      }, 100);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update community group");
    },
  });

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields before submission
    if (!validateAllFields()) {
      return;
    }

    let imageUrl = image;
    if (imageFile) {
      try {
        const fd = new FormData();
        fd.append("file", imageFile);
        const uploadRes = await uploadService.uploadFile(fd);
        if (typeof uploadRes?.data === "string") imageUrl = uploadRes.data;
        else if (uploadRes?.data?.url) imageUrl = (uploadRes.data as any).url;
      } catch (err: any) {
        showError(err?.response?.data?.message || "Failed to upload image");
        return;
      }
    }

    // Prepare payload
    const payload = {
      name: groupName.trim(),
      description: description.trim(),
      categoryId: category,
      memberCount: Number(members),
      status: status,
      ...(imageUrl ? { image: imageUrl } : {}),
    };

    // Call API - update if edit mode, create otherwise
    if (isEditMode && groupId) {
      updateCommunityGroup({ id: groupId, data: payload });
    } else {
      createCommunityGroup(payload);
    }
  };

  // Handle field changes with validation
  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGroupName(value);
    if (errors.groupName) {
      const error = validateGroupName(value);
      setErrors((prev) => ({ ...prev, groupName: error || undefined }));
    }
  };

  const handleCategoryChange = (e: any) => {
    const value = e.target.value;
    setCategory(value);
    if (errors.category) {
      const error = validateCategory(value);
      setErrors((prev) => ({ ...prev, category: error || undefined }));
    }
  };

  const handleMembersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMembers(value);
    if (errors.members) {
      const error = validateMembers(value);
      setErrors((prev) => ({ ...prev, members: error || undefined }));
    }
  };

  // Handle blur events to validate fields
  const handleGroupNameBlur = () => {
    const error = validateGroupName(groupName);
    setErrors((prev) => ({ ...prev, groupName: error || undefined }));
  };

  const handleCategoryBlur = () => {
    const error = validateCategory(category);
    setErrors((prev) => ({ ...prev, category: error || undefined }));
  };

  const handleMembersBlur = () => {
    const error = validateMembers(members);
    setErrors((prev) => ({ ...prev, members: error || undefined }));
  };

  return (
    <>
      <div className="main_title">
        <h1>{isEditMode ? "Edit Community Group" : "Add Community Group"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          <button onClick={() => navigate("/Community")}>
            Manage Community Groups
          </button>
          - {isEditMode ? "Edit Group" : "Add Group"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={handleSubmit}>
          <div className="gap_p">
            {/* Group Name */}
            <div className="control_group w_33">
              <label>Group Name*</label>
              <TextField
                placeholder="Enter Group Name"
                fullWidth
                value={groupName}
                onChange={handleGroupNameChange}
                onBlur={handleGroupNameBlur}
                error={!!errors.groupName}
              />
              {errors.groupName && (
                <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                  {errors.groupName}
                </FormHelperText>
              )}
            </div>

            {/* Group Image (optional) */}
            <div className="control_group w_33">
              <label>Group Image</label>
              {image ? (
                <div className="upload_image v2 no_radius">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="Group" style={{ maxHeight: 120, objectFit: "contain" }} />
                    </figure>
                    <IconButton
                      aria-label="Remove image"
                      onClick={() => {
                        setImage("");
                        setImageFile(null);
                      }}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              ) : (
                <label className="upload_image v2 no_radius" htmlFor="communityGroupImageUpload">
                  <Input
                    id="communityGroupImageUpload"
                    sx={{ display: "none" }}
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg,image/jpg,image/webp" }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const files = event.target.files;
                      if (files && files[0]?.type?.includes("image")) {
                        setImage(URL.createObjectURL(files[0]));
                        setImageFile(files[0]);
                      }
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="Upload group image" />
                    </figure>
                  </span>
                </label>
              )}
            </div>

            {/* Description */}
            <div className="control_group w_100">
              <label>Description*</label>
              <EditText
                content={description}
                setContent={(html) => {
                  setDescription(html);
                  if (errors.description) {
                    setErrors((prev) => ({ ...prev, description: validateDescription(html) || undefined }));
                  }
                }}
              />
              {errors.description && (
                <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                  {errors.description}
                </FormHelperText>
              )}
            </div>

            {/* Category */}
            <div className="control_group w_33">
              <label>Category*</label>
              <FormControl fullWidth error={!!errors.category}>
                <Select
                  value={category}
                  onChange={handleCategoryChange}
                  onBlur={handleCategoryBlur}
                  disabled={isLoadingCategories}
                >
                  <MenuItem disabled value="default">
                    {isLoadingCategories ? "Loading..." : "Select Category"}
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                    {errors.category}
                  </FormHelperText>
                )}
              </FormControl>
            </div>

            {/* Member Count */}
            <div className="control_group w_33">
              <label>Members Count*</label>
              <TextField
                placeholder="Enter Member Count"
                fullWidth
                type="number"
                value={members}
                onChange={handleMembersChange}
                onBlur={handleMembersBlur}
                error={!!errors.members}
              />
              {errors.members && (
                <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                  {errors.members}
                </FormHelperText>
              )}
            </div>

            

            {/* Status Toggle */}
            <div className="control_group w_33">
              <label>Status</label>
              <div style={{ marginTop: "8px" }}>
                <input
                  type="checkbox"
                  checked={status}
                  onChange={() => setStatus(!status)}
                />{" "}
                Active
              </div>
            </div>

            
          </div>

          {/* Save Button */}
          <div className="form_btn">
            <Button 
              type="submit"
              className="btn btn_primary"
              disabled={isCreating || isUpdating || isLoadingGroup}
            >
              {isLoadingGroup ? "Loading..." : isCreating || isUpdating ? "Saving..." : isEditMode ? "Update" : "Save"}
            </Button>
          </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddCommunityGroup;
